import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CheckCircle2, CalendarDays, ArrowLeft, Mail, Phone } from 'lucide-react';
import { CALENDLY, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from '../components/home/homeData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

const useCalendly = (app, open) => {
  const [booked, setBooked] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    if (!document.querySelector(`script[src="${SCRIPT}"]`)) {
      const s = document.createElement('script'); s.src = SCRIPT; s.async = true; document.body.appendChild(s);
    }
    const onMsg = (e) => {
      if (e.origin !== 'https://calendly.com' || e.data?.event !== 'calendly.event_scheduled') return;
      setBooked(true);
      if (app?.id) axios.post(`${API}/applications/${app.id}/scheduled`, { token: app.token, eventUri: e.data.payload?.event?.uri || '' }).catch(() => {});
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [open, app]);
  return booked;
};

export default function ApplicationReceived() {
  const [app] = useState(() => { try { return JSON.parse(sessionStorage.getItem('yv-app')); } catch { return null; } });
  const [open, setOpen] = useState(false);
  const booked = useCalendly(app, open);
  useEffect(() => { document.title = 'Application Received | YOVAI'; window.scrollTo(0, 0); }, []);
  const url = `${CALENDLY}?hide_gdpr_banner=1${app?.name ? `&name=${encodeURIComponent(app.name)}` : ''}${app?.email ? `&email=${encodeURIComponent(app.email)}` : ''}`;
  return (
    <div className="landing-page yv-home yv-page" data-testid="application-received-page">
      <header className="yv-nav is-scrolled"><div className="yv-container yv-nav-inner">
        <Link to="/" className="yv-nav-logo" data-testid="received-logo"><img src="/yovai-logo-light.png" alt="YOVAI" /></Link>
        <Link to="/" className="yv-nav-contact" style={{ display: 'inline-flex' }} data-testid="received-home-link"><ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />Back To Home</Link>
      </div></header>
      <main className="yv-section yv-dark yv-grid-dark">
        <div className="yv-container yv-received">
          <div className="yv-received-card" data-testid="application-confirmation">
            <CheckCircle2 className="w-10 h-10 yv-received-icon" aria-hidden="true" />
            <p className="yv-eyebrow">Application Received</p>
            <h1 className="yv-h2">Thank You{app?.name ? `, ${app.name.split(' ')[0]}` : ''}. <span className="yv-blue-ink yv-block">Your Application Is In Review.</span></h1>
            <p className="yv-lede mt-5">Our Team Reviews Every Application Personally. We Will Contact You By Email Or Phone, Typically Within One To Two Business Days, To Discuss Your Operations And Next Steps.</p>
            <ol className="yv-received-steps">
              <li><span className="yv-mono">01</span>Application Review</li>
              <li><span className="yv-mono">02</span>Discovery Call</li>
              <li><span className="yv-mono">03</span>Tailored Recommendation</li>
            </ol>
            {booked ? (
              <p className="yv-form-ok" data-testid="received-booked">Your Discovery Call Is Scheduled. A Confirmation Email Is On Its Way.</p>
            ) : !open && (
              <div className="yv-hero-actions">
                <button onClick={() => setOpen(true)} className="yv-btn yv-btn-primary" data-testid="received-schedule-btn"><CalendarDays className="w-4 h-4" aria-hidden="true" />Schedule Your Discovery Call (Optional)</button>
                <Link to="/" className="yv-btn yv-btn-ghost" data-testid="received-back-btn">Return To Homepage</Link>
              </div>
            )}
            <div className="yv-received-contact">
              <a href={CONTACT_PHONE_HREF} data-testid="received-phone"><Phone className="w-4 h-4" aria-hidden="true" />{CONTACT_PHONE}</a>
              <a href={`mailto:${CONTACT_EMAIL}`} className="yv-keep-case" data-testid="received-email"><Mail className="w-4 h-4" aria-hidden="true" />{CONTACT_EMAIL}</a>
            </div>
          </div>
          {open && <div className="calendly-inline-widget yv-received-cal" data-url={url} data-testid="received-calendly" />}
        </div>
      </main>
    </div>
  );
}
